
import React, {
  memo,
  ReactElement,
  useCallback,
  useMemo,
  useState,
  useEffect
} from "react";
import {
  Grow,
  Tooltip,
  TooltipProps,
  useTheme
} from "@material-ui/core";
import {useTranslation} from "react-i18next";

type TooltipPosition = {
  x: number;
  y: number;
}

export const ArrowTooltip = (props: TooltipProps) => {
  return (
    <Tooltip
      arrow
      {...props}
    >
      {props.children as any}
    </Tooltip>
  );
};

interface CursorTooltipProps {
  textLines: string[]
  children: ReactElement,
  ignoreToggle?: boolean
}

export const CursorTooltip = memo((props: CursorTooltipProps) => {
  const {textLines, children, ignoreToggle} = props;

  const theme = useTheme();
  const {t} = useTranslation();

  const [hideTooltips, setHideTooltips] = useState(false);
  const [position, setPosition] = useState<TooltipPosition>({
    x: undefined,
    y: undefined
  });

  const title = useMemo(() => {
    const modifiedLines = [...textLines];
    if ((textLines.length > 0) && !ignoreToggle) {
      modifiedLines.push(t('tooltip.toggle-tooltip'));
    }

    return modifiedLines.map((line, index) => (
      <React.Fragment key={index}>
        {(index !== 0) && (<br/>)}
        {line}
      </React.Fragment>
    ));
  }, [textLines]);

  const onClose = useCallback(() => {
    setPosition({
      x: undefined,
      y: undefined
    });
    setHideTooltips(false);
  }, []);

  const onMouseMove = useCallback((event) => {
    setPosition({
      x: event.pageX,
      y: event.pageY
    });
  }, []);

  const {enteringScreen} = theme.transitions.duration;

  const transitionProps = {
    timeout: {
      enter: enteringScreen,
      exit: 0
    }
  };

  /** Tooltip toggle hotkey event listener */
  useEffect(() => {
    if (!ignoreToggle) {
      if (textLines.length === 0) {
        setHideTooltips(false);
        return;
      }
      const onHotkey = (event => {
        if (event.target instanceof HTMLInputElement) {
          return;
        }
        if (event.which === 192) {
          setHideTooltips(!hideTooltips);
        }
      });

      window.addEventListener('keydown', onHotkey, false);
      return () => {
        window.removeEventListener('keydown', onHotkey, false);
      };
    }
  }, [textLines, hideTooltips]);

  const visible = (textLines.length > 0)
    && (position.x !== undefined) && (!hideTooltips);

  return (
    <Tooltip
      arrow
      open={visible}
      title={title}
      placement="right"
      onClose={onClose}
      onMouseMove={onMouseMove}
      TransitionComponent={Grow}
      TransitionProps={transitionProps}
      PopperProps={{
        anchorEl: {
          clientHeight: 0,
          clientWidth: 0,
          getBoundingClientRect: () => ({
            top: position.y,
            left: position.x,
            right: position.x,
            bottom: position.y,
            width: 0,
            height: 0
          } as any)
        }
      }}
    >
      {children}
    </Tooltip>
  );
});